import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Navbar from '../../Navbar';
import Coins from '../../../assets/img/coins.svg';
import Next from '../../../assets/img/next.svg';
import Previous from '../../../assets/img/previous.svg';
import { gameActions, profileActions } from '../../../actions';
import { prefetchService, showToast } from '../../../services';
import { useLoggedUser } from '../../../hooks/useLoggedUser';
import Close from '../../../assets/img/close.svg';

const GAMES_PER_PAGE = 14;

const HomePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const loggedUser = useLoggedUser();

  const { games, blockedstatus, error, gamesLoading, blockedStatusLoading, hasLoadedGames, hasLoadedBlockedStatus } = useSelector((state) => state.games);
  const { appconfigs } = useSelector(state => state.config);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGame, setSelectedGame] = useState(null);

  const showPaidGameConfig = appconfigs?.find(config => config.ConfigName === 'SHOW_PAID_GAME');
  const showPaidGame = showPaidGameConfig?.ConfigValue || 'No';

  const studentProfileId = loggedUser?.data?.StudentProfileID;
  const gameMenu = loggedUser?.data?.GameMenu;

  if (!studentProfileId) {
    history.replace('/login');
    return null;
  }

  const retryLoadGames = useCallback(() => {
    if (studentProfileId && gameMenu != null) {
      dispatch(gameActions.fetchGames(studentProfileId, gameMenu, true));
      dispatch(gameActions.fetchBlockedStatus(studentProfileId, true));
    }
  }, [dispatch, studentProfileId, gameMenu]);

  useEffect(() => {
    if (location.pathname !== '/') return;
    if (!studentProfileId || gameMenu == null) return;

    dispatch(gameActions.fetchGames(studentProfileId, gameMenu, true));
    dispatch(gameActions.fetchBlockedStatus(studentProfileId, true));
  }, [dispatch, location.pathname, studentProfileId, gameMenu]);

  useEffect(() => {
    if (selectedGame) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [selectedGame]);

  const goToGameQuestion = useCallback((gameId, gameName) => {
    history.push({
      pathname: '/gamequestion',
      state: { gameId, gameName },
    });
  }, [history]);

  const purchaseGame = useCallback((gameType, gameId, paidCoins) => {
    dispatch(gameActions.purchaseGame({
      GameType: gameType,
      GameID: gameId,
      StudentProfileID: studentProfileId,
      PaidCoinCount: paidCoins
    }))
      .then((response) => {
        dispatch(gameActions.fetchGames(studentProfileId, gameMenu));
        dispatch(profileActions.fetchMypoints(studentProfileId));
      })
      .catch((err) => {
        showToast(err?.message || err, 'error');
      });
  }, [dispatch, studentProfileId, gameMenu]);

  const prefetchGameQuestion = useCallback((game) => {
    if (!game || !studentProfileId) return;
    if (!(game.GameType === "free" || game.isPurchased === "Y")) return;

    prefetchService.prefetchGameQuestionEntry({
      studentProfileId,
      gameId: game.GameID,
    });
  }, [studentProfileId]);

  const handleGameClick = useCallback((game) => {
    if (game.GameType === "free" || game.isPurchased === "Y") {
      prefetchGameQuestion(game);
      goToGameQuestion(game.GameID, game.GameName);
    } else if (game.GameType === "paid" && game.isPurchased !== "Y") {
      setSelectedGame(game);
    }
  }, [goToGameQuestion, prefetchGameQuestion]);

  const confirmPurchase = useCallback(() => {
    if (selectedGame) {
      purchaseGame(selectedGame.GameType, selectedGame.GameID, selectedGame.GamePrice);
      setSelectedGame(null);
    }
  }, [selectedGame, purchaseGame]);

  const sortedGames = useMemo(() => {
    if (!Array.isArray(games)) return [];
    return [...games].sort((a, b) => {
      if (a.GameType === "free" && b.GameType !== "free") return -1;
      if (a.GameType !== "free" && b.GameType === "free") return 1;
      if (a.GameType === "paid" && a.isPurchased === "Y" && b.isPurchased !== "Y") return -1;
      if (a.GameType === "paid" && a.isPurchased !== "Y" && b.isPurchased === "Y") return 1;
      return 0;
    });
  }, [games]);

  const filteredGames = useMemo(() => {
    if (showPaidGame.toLowerCase() === 'yes') return sortedGames;
    return sortedGames.filter(game => !(game.GameType === "paid" && game.isPurchased !== "Y"));
  }, [sortedGames, showPaidGame]);

  const startIndex = (currentPage - 1) * GAMES_PER_PAGE;
  const paginatedGames = useMemo(() => {
    return filteredGames.slice(startIndex, startIndex + GAMES_PER_PAGE);
  }, [filteredGames, startIndex]);

  const LoadNextPage = useCallback(() => {
    setCurrentPage(prevPage => {
      if (startIndex + GAMES_PER_PAGE < filteredGames.length) return prevPage + 1;
      return prevPage;
    });
  }, [startIndex, filteredGames.length]);

  const LoadPreviousPage = useCallback(() => {
    setCurrentPage(prevPage => (prevPage > 1 ? prevPage - 1 : prevPage));
  }, []);

  const showBlockedStatusLoading = blockedStatusLoading && !hasLoadedBlockedStatus;
  const showGamesLoading = gamesLoading && !hasLoadedGames;

  return (
    <React.Fragment>
      <div className="container-fluid games-btn-page">
        <Navbar />
        <div className="content" id="content">
          <div className="d-flex justify-content-center align-items-center flex-column">
            <h2 className="b-font game-mm-h main-heading-top"> {loggedUser?.data?.BlockGame ?? ''} Game Main Menu</h2>

            {showBlockedStatusLoading ? (
              <div className='blocked-msg'>Loading...</div>
            ) : blockedstatus?.BlockGame ? (
              <div className='blocked-msg'>You have been blocked from the game. Please contact your admin to regain access.</div>
            ) : (
              <>
                <div className="game-btns-cont w-100 d-flex flex-column align-items-center mt-1">
                  {showGamesLoading ? (
                    <div className="blocked-msg">Loading...</div>
                  ) : error && filteredGames.length === 0 ? (
                    <div className="blocked-msg d-flex flex-column align-items-center">
                      <span className="mb-2">Unable to load games. Please try again.</span>
                      <button type="button" className="accept-btn mt-2" onClick={retryLoadGames}>Retry</button>
                    </div>
                  ) : Array.isArray(paginatedGames) && paginatedGames.length > 0 ? (
                    <>
                      {(() => {
                        const rows = [];
                        let index = 0;
                        let isOddRow = true;

                        while (index < paginatedGames.length) {
                          const gamesPerRow = isOddRow ? 3 : 4;
                          const rowGames = paginatedGames.slice(index, index + gamesPerRow);
                          const rowStart = index;
                          index += gamesPerRow;
                          isOddRow = !isOddRow;

                          rows.push(
                            <div
                              className={`row d-flex align-items-center justify-content-center mt-1 ${gamesPerRow === 3 ? "w-75" : "w-100"}`}
                              key={`row-${rowStart}`}
                            >
                              {rowGames.map((game) => (
                                <div
                                  className={`slct-gme ${gamesPerRow === 3 ? "col-sm-4 col-lg-4 col-md-4" : "col-sm-3 col-lg-3 col-md-3"}`}
                                  key={game.GameID}
                                >
                                  <div
                                    className={`d-flex flex-row align-items-center slct-gme-div ${game.isPurchased === "Y" && game.GameType === "paid" ? 'purchased-game' : ''} ${game.Is_Played === "Y" ? 'played-game' : ''} justify-content-center`}
                                    onClick={() => handleGameClick(game)}
                                    onMouseEnter={() => prefetchGameQuestion(game)}
                                    onFocus={() => prefetchGameQuestion(game)}
                                    onTouchStart={() => prefetchGameQuestion(game)}
                                  >
                                    <label className={`slct-gm-name text-center ${game.isPurchased !== "Y" && game.GameType === "paid" ? "buying-gme" : ""}`}>
                                      {game.GameName}
                                    </label>
                                    {game.GameType === "paid" && game.isPurchased !== "Y" && (
                                      <div className="gm-cns-div">
                                        <div className="gm-cns">
                                          <img src={Coins} className="w-50" alt="Coins" />
                                          <label className="game-coins">Buy</label>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {game.GameType === "paid" && game.isPurchased !== "Y" ? (
                                    <h3 className="s-g-text mt-1 text-center">{game.GamePrice} coins</h3>
                                  ) : (
                                    <h3 className="s-g-text mt-1 text-center">&nbsp;</h3>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return rows;
                      })()}

                    </>
                  ) : (
                    <div className="blocked-msg d-flex flex-column align-items-center">
                      <span className="mb-2">{hasLoadedGames ? 'No games available.' : 'Loading...'}</span>
                      <button type="button" className="accept-btn mt-2" onClick={retryLoadGames}>Retry</button>
                    </div>
                  )}
                </div>

                <div className='gme-q-pn-left d-flex align-items-center justify-content-between p-2'>
                  <div>
                    {currentPage > 1 && (
                      <img src={Previous} onClick={LoadPreviousPage} alt="Previous" />
                    )}
                  </div>
                </div>
                <div className='gme-q-pn-right d-flex align-items-center justify-content-between p-2'>
                  <div>
                    {startIndex + GAMES_PER_PAGE < filteredGames.length && (
                      <img src={Next} onClick={LoadNextPage} alt="Next" />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modal starts here */}
        {selectedGame && (
          <div className="modal alrt-mdl" id="alert-modal" style={{ display: 'block' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body ad-modal pt-5">
                  <div className="w-100 d-flex align-items-center justify-content-center">
                    <label className='b-font alt-label'>
                      Are you sure you want to purchase this game?
                    </label>
                  </div>
                  <div className="pl-5 mt-4 mb-4 d-flex align-items-center justify-content-between">
                    <button className="accept-btn mt-2" onClick={confirmPurchase} data-bs-dismiss="modal">Yes</button>
                    <button className="accept-btn no mt-2" onClick={() => setSelectedGame(null)} data-bs-dismiss="modal">No</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export { HomePage };
